"use client";

import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocale, useTranslations } from "next-intl";
import { CheckCircle2 } from "lucide-react";
import { FloatField, floatInputClass } from "@isg/booking/field-shell";
import { CountrySelect } from "@isg/booking/country-select";
import { PhoneInput } from "@isg/booking/phone-input";
import {
  makeCarrierRegistrationSchema,
  type CarrierRegistrationData,
} from "@/lib/forms/schemas";
import { pushLeadSubmit } from "@/lib/analytics/gtm";
import { cn } from "@/lib/cn";

const VEHICLE_TYPES = ["pkw", "sprinter", "lkw75", "lkw12", "lkw40"] as const;

type DocKey = "euLicense" | "insurance" | "trade";

/** Fields validated per step before "Weiter" advances the wizard. */
const STEP_FIELDS: (keyof CarrierRegistrationData)[][] = [
  ["company", "country", "ustId", "gewerbeConfirm"],
  ["salutation", "firstName", "lastName", "email", "phone", "street", "zip", "city"],
  ["fleet", "region"],
  ["documents", "insuranceConfirm", "privacyConsent"],
];

export function CarrierRegistration() {
  const locale = useLocale();
  const t = useTranslations("CarrierForm");
  const tv = useTranslations("Validation");
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [uploading, setUploading] = useState<Partial<Record<DocKey, boolean>>>({});
  const [uploadErr, setUploadErr] = useState<Partial<Record<DocKey, boolean>>>({});

  const schema = useMemo(
    () =>
      makeCarrierRegistrationSchema({
        phoneInvalid: tv("phoneInvalid"),
        phoneChars: tv("phoneChars"),
      }),
    [tv],
  );

  const { register, handleSubmit, trigger, watch, setValue, formState } =
    useForm<CarrierRegistrationData>({
      resolver: zodResolver(schema),
      mode: "onTouched",
      defaultValues: {
        company: "",
        country: "DE",
        ustId: "",
        gewerbeConfirm: false,
        salutation: undefined,
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        street: "",
        zip: "",
        city: "",
        fleet: [],
        region: "",
        documents: undefined,
        insuranceConfirm: false,
        privacyConsent: false,
      },
    });

  const errors = formState.errors;
  const isLast = step === STEP_FIELDS.length - 1;
  const stepHasError = STEP_FIELDS[step].some((f) => errors[f]);

  /** Localized per-field error, same pattern as the booking flow
   *  (`tv("pleaseEnter", { field })`). */
  const fieldError = (key: keyof CarrierRegistrationData, label: string) =>
    errors[key] ? tv("pleaseEnter", { field: label }) : undefined;

  async function uploadDoc(key: DocKey, file: File) {
    setUploading((u) => ({ ...u, [key]: true }));
    setUploadErr((e) => ({ ...e, [key]: false }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("docType", key);
      const res = await fetch("/api/carrier-upload", { method: "POST", body: fd });
      if (!res.ok) throw new Error("upload failed");
      const { url } = (await res.json()) as { url: string };
      setValue(`documents.${key}` as const, url, { shouldValidate: true });
    } catch {
      setUploadErr((e) => ({ ...e, [key]: true }));
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  }

  async function goNext() {
    const ok = await trigger(STEP_FIELDS[step]);
    if (ok) setStep((s) => Math.min(s + 1, STEP_FIELDS.length - 1));
  }

  function goBack() {
    setStep((s) => Math.max(s - 1, 0));
  }

  async function onSubmit(data: CarrierRegistrationData) {
    let ok = false;
    try {
      const res = await fetch("/api/driver-apply", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...data, locale }),
      });
      ok = res.ok;
    } finally {
      setDone(true);
      if (ok) {
        await pushLeadSubmit({
          form: "carrier",
          userData: {
            email: data.email,
            phone: data.phone,
            firstName: data.firstName,
            lastName: data.lastName,
          },
        });
      }
    }
  }

  if (done) {
    return (
      <div className="surface p-6 md:p-8">
        <p className="inline-flex items-center gap-2 text-lg font-semibold text-success-500">
          <CheckCircle2 className="h-6 w-6 shrink-0" aria-hidden /> {t("successTitle")}
        </p>
        <p className="mt-3 max-w-[52ch] text-base leading-relaxed text-ink-700">
          {t("successBody")}
        </p>
      </div>
    );
  }

  const stepLabels = [t("step1"), t("step2"), t("step3"), t("step4")];
  const vtLabel: Record<(typeof VEHICLE_TYPES)[number], string> = {
    pkw: t("vt_pkw"),
    sprinter: t("vt_sprinter"),
    lkw75: t("vt_lkw75"),
    lkw12: t("vt_lkw12"),
    lkw40: t("vt_lkw40"),
  };

  // Fleet: a count per selected vehicle type.
  const fleet = watch("fleet") ?? [];
  const fleetHas = (vt: (typeof VEHICLE_TYPES)[number]) => fleet.some((f) => f.type === vt);
  const fleetCount = (vt: (typeof VEHICLE_TYPES)[number]) =>
    fleet.find((f) => f.type === vt)?.count ?? 1;
  const toggleVt = (vt: (typeof VEHICLE_TYPES)[number], on: boolean) =>
    setValue(
      "fleet",
      on
        ? [...fleet.filter((f) => f.type !== vt), { type: vt, count: fleetCount(vt) }]
        : fleet.filter((f) => f.type !== vt),
      { shouldValidate: true },
    );
  const setVtCount = (vt: (typeof VEHICLE_TYPES)[number], n: number) =>
    setValue("fleet", fleet.map((f) => (f.type === vt ? { ...f, count: n } : f)), {
      shouldValidate: true,
    });
  const documents = watch("documents");

  return (
    <div>
      <header className="mb-6">
        <h2 className="text-2xl font-semibold tracking-tight text-ink-900">
          {t("title")}
        </h2>
        <p className="mt-2 max-w-[58ch] text-base leading-relaxed text-ink-700">
          {t("intro")}
        </p>
      </header>

      {/* Slim progress bar */}
      <ol className="mb-7 grid grid-cols-4 gap-2">
        {stepLabels.map((label, i) => (
          <li key={label} className="flex flex-col gap-1.5">
            <span
              className={cn(
                "h-1 rounded-full transition-colors",
                i <= step ? "bg-brand-600" : "bg-line-200",
              )}
            />
            <span
              className={cn(
                "text-xs font-medium",
                i === step ? "text-ink-900" : "text-ink-500",
              )}
            >
              <span className="tabular-nums">{i + 1}.</span> {label}
            </span>
          </li>
        ))}
      </ol>

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-5">
        {/* ── Step 1 · Unternehmen ───────────────────────────────── */}
        {step === 0 ? (
          <section className="grid gap-5">
            <h3 className="text-base font-semibold text-ink-900">{t("companyTitle")}</h3>
            <FloatField label={t("company")} required error={fieldError("company", t("company"))}>
              <input
                autoComplete="organization"
                placeholder={t("companyPlaceholder")}
                className={floatInputClass({ invalid: !!errors.company })}
                {...register("company")}
              />
            </FloatField>
            <div className="grid gap-5 sm:grid-cols-2">
              {/* Reuses the booking flow's searchable country dropdown 1:1. */}
              <FloatField label={t("country")}>
                <CountrySelect
                  value={watch("country") ?? "DE"}
                  onChange={(c) => setValue("country", c, { shouldValidate: true })}
                />
              </FloatField>
              <FloatField label={t("ustId")}>
                <input
                  placeholder={t("ustIdPlaceholder")}
                  className={floatInputClass({ invalid: !!errors.ustId })}
                  {...register("ustId")}
                />
              </FloatField>
            </div>
            <label
              className={cn(
                "flex cursor-pointer items-start gap-2.5 rounded-md border bg-white p-3.5 transition-colors",
                errors.gewerbeConfirm ? "border-danger-500" : "border-line-200",
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-ink-900"
                {...register("gewerbeConfirm")}
              />
              <span className="text-sm leading-relaxed text-ink-700">
                {t("gewerbeConfirm")}
              </span>
            </label>
          </section>
        ) : null}

        {/* ── Step 2 · Kontakt ───────────────────────────────────── */}
        {step === 1 ? (
          <section className="grid gap-5">
            <h3 className="text-base font-semibold text-ink-900">{t("contactTitle")}</h3>
            <div className="grid gap-5 sm:grid-cols-2">
              <FloatField label={t("salutation")}>
                <select
                  className={floatInputClass({ invalid: !!errors.salutation })}
                  {...register("salutation", {
                    setValueAs: (v) => (v === "" ? undefined : v),
                  })}
                >
                  <option value="">{t("salutationSelect")}</option>
                  <option value="mr">{t("sal_mr")}</option>
                  <option value="ms">{t("sal_ms")}</option>
                  <option value="diverse">{t("sal_diverse")}</option>
                </select>
              </FloatField>
              <div className="hidden sm:block" aria-hidden />
              <FloatField label={t("firstName")} required error={fieldError("firstName", t("firstName"))}>
                <input
                  autoComplete="given-name"
                  placeholder={t("firstNamePlaceholder")}
                  className={floatInputClass({ invalid: !!errors.firstName })}
                  {...register("firstName")}
                />
              </FloatField>
              <FloatField label={t("lastName")} required error={fieldError("lastName", t("lastName"))}>
                <input
                  autoComplete="family-name"
                  placeholder={t("lastNamePlaceholder")}
                  className={floatInputClass({ invalid: !!errors.lastName })}
                  {...register("lastName")}
                />
              </FloatField>
              <FloatField label={t("email")} required error={fieldError("email", t("email"))}>
                <input
                  type="email"
                  autoComplete="email"
                  placeholder={t("emailPlaceholder")}
                  className={floatInputClass({ invalid: !!errors.email })}
                  {...register("email")}
                />
              </FloatField>
              <FloatField label={t("phone")} required error={fieldError("phone", t("phone"))}>
                {/* Reuses the booking flow's phone field (dial-code picker) 1:1. */}
                <PhoneInput
                  value={watch("phone") ?? ""}
                  onChange={(v) => setValue("phone", v, { shouldValidate: true })}
                  invalid={!!errors.phone}
                  placeholder=""
                />
              </FloatField>
            </div>
            <FloatField label={t("street")} required error={fieldError("street", t("street"))}>
              <input
                autoComplete="street-address"
                placeholder={t("streetPlaceholder")}
                className={floatInputClass({ invalid: !!errors.street })}
                {...register("street")}
              />
            </FloatField>
            <div className="grid gap-5 sm:grid-cols-[1fr_2fr]">
              <FloatField label={t("zip")} required error={fieldError("zip", t("zip"))}>
                <input
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder={t("zipPlaceholder")}
                  className={floatInputClass({ invalid: !!errors.zip })}
                  {...register("zip")}
                />
              </FloatField>
              <FloatField label={t("city")} required error={fieldError("city", t("city"))}>
                <input
                  autoComplete="address-level2"
                  placeholder={t("cityPlaceholder")}
                  className={floatInputClass({ invalid: !!errors.city })}
                  {...register("city")}
                />
              </FloatField>
            </div>
          </section>
        ) : null}

        {/* ── Step 3 · Fuhrpark ──────────────────────────────────── */}
        {step === 2 ? (
          <section className="grid gap-5">
            <h3 className="text-base font-semibold text-ink-900">{t("fleetTitle")}</h3>
            <fieldset className="grid gap-2">
              <legend className="label">{t("vehicleTypes")}</legend>
              <p className="mb-1 text-xs text-ink-500">{t("fleetHint")}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {VEHICLE_TYPES.map((vt) => {
                  const on = fleetHas(vt);
                  return (
                    <div
                      key={vt}
                      className={cn(
                        "flex items-center gap-2.5 rounded-md border bg-white p-3 transition-colors",
                        errors.fleet ? "border-danger-500" : "border-line-200",
                        on && "border-ink-900/40",
                      )}
                    >
                      <label htmlFor={`vt-${vt}`} className="flex flex-1 cursor-pointer items-center gap-3">
                        <input
                          id={`vt-${vt}`}
                          type="checkbox"
                          checked={on}
                          onChange={(e) => toggleVt(vt, e.target.checked)}
                          className="h-4 w-4 accent-ink-900"
                        />
                        <span className="text-sm text-ink-900">{vtLabel[vt]}</span>
                      </label>
                      {on ? (
                        <input
                          type="number"
                          min={1}
                          inputMode="numeric"
                          aria-label={`${vtLabel[vt]} – ${t("vehicleCount")}`}
                          value={fleetCount(vt)}
                          onChange={(e) =>
                            setVtCount(vt, Math.max(1, parseInt(e.target.value || "1", 10) || 1))
                          }
                          className="w-16 rounded-md border border-line-200 px-2 py-1.5 text-right text-sm tabnums"
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {errors.fleet ? <p className="text-xs text-danger-500">{t("requiredFields")}</p> : null}
            </fieldset>
            <FloatField label={t("region")} required error={fieldError("region", t("region"))}>
              <input
                placeholder={t("regionPlaceholder")}
                className={floatInputClass({ invalid: !!errors.region })}
                {...register("region")}
              />
            </FloatField>
          </section>
        ) : null}

        {/* ── Step 4 · Nachweise & Einwilligung ──────────────────── */}
        {step === 3 ? (
          <section className="grid gap-4">
            <h3 className="text-base font-semibold text-ink-900">{t("proofTitle")}</h3>

            <div className="grid gap-2.5">
              <p className="text-sm font-medium text-ink-900">{t("docsTitle")}</p>
              {(
                [
                  ["euLicense", t("doc_euLicense")],
                  ["insurance", t("doc_insurance")],
                  ["trade", t("doc_trade")],
                ] as [DocKey, string][]
              ).map(([key, label]) => {
                const url = documents?.[key];
                return (
                  <div
                    key={key}
                    className={cn(
                      "flex flex-wrap items-center gap-3 rounded-md border bg-white p-3.5 transition-colors",
                      uploadErr[key] ? "border-danger-500" : url ? "border-success-500/50" : "border-line-200",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink-900">{label}</p>
                      <p className="truncate text-xs text-ink-500">
                        {uploadErr[key] ? t("uploadError") : url ? t("uploaded") : t("docHint")}
                      </p>
                    </div>
                    <label className="btn-secondary btn-sm cursor-pointer whitespace-nowrap">
                      {uploading[key] ? t("uploading") : url ? t("uploadChange") : t("uploadCta")}
                      <input
                        type="file"
                        accept="application/pdf,image/jpeg,image/png"
                        className="sr-only"
                        disabled={uploading[key]}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadDoc(key, f);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                );
              })}
            </div>

            <label
              className={cn(
                "flex cursor-pointer items-start gap-2.5 rounded-md border bg-white p-3.5 transition-colors",
                errors.insuranceConfirm ? "border-danger-500" : "border-line-200",
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-ink-900"
                {...register("insuranceConfirm")}
              />
              <span className="text-sm leading-relaxed text-ink-700">
                {t("insuranceConfirm")}
              </span>
            </label>
            <label
              className={cn(
                "flex cursor-pointer items-start gap-2.5 rounded-md border bg-white p-3.5 transition-colors",
                errors.privacyConsent ? "border-danger-500" : "border-line-200",
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-ink-900"
                {...register("privacyConsent")}
              />
              <span className="text-sm leading-relaxed text-ink-700">
                {t("privacyConsent")}
              </span>
            </label>
          </section>
        ) : null}

        {stepHasError ? (
          <p className="text-xs text-danger-500">{t("requiredFields")}</p>
        ) : null}

        <div className="mt-1 flex items-center justify-between gap-3 border-t border-line-200 pt-5">
          {step > 0 ? (
            <button type="button" className="btn-secondary btn-lg" onClick={goBack}>
              {t("back")}
            </button>
          ) : (
            <span />
          )}
          {isLast ? (
            <button type="submit" className="btn-primary btn-lg">
              {t("submit")}
            </button>
          ) : (
            <button type="button" className="btn-primary btn-lg" onClick={goNext}>
              {t("next")}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
